import Dexie, { type EntityTable, type Transaction } from "dexie";
import { INTERNAL_SCHEMA_VERSION } from "../domain/common";
import { createDefaultConstitution } from "../domain/constitution-v1";
import type {
  AuditEvent,
  CEOProfile,
  ConstitutionVersion,
  DataExportManifest,
  DataImportResult,
  DatabaseMetaRecord,
  ExternalSummaryRecord,
  PrivateSourceRecord,
  RecordCounts,
  StoredBoardContext,
  StoredJointCorrection,
  StructuredCEORecord,
} from "../domain/types";

export const DATABASE_NAME = "ceo-os-local";
export const DATABASE_VERSION = 4;

const VERSION_1_STORES = {
  privateSources:
    "id, recordType, occurredAt, createdAt, updatedAt, sourceMode, sensitivity, retentionPolicy, exportAllowed",
  structuredRecords:
    "id, recordType, effectiveDate, createdAt, updatedAt, status, exportAllowed, *sourceRecordIds",
  externalSummaries:
    "id, summaryType, date, createdAt, updatedAt, status, exportAllowed, *sourceStructuredRecordIds",
  constitutions: "id, version, status, createdAt, updatedAt",
  profiles: "id, status, createdAt, updatedAt",
  boardImports: "id, contractId, date, importedAt, payloadHash, status",
  jointCorrections: "id, date, status, boardContextId, ceoReviewExportId",
  auditEvents: "id, occurredAt, entityType, entityId, action",
  exportManifests: "id, exportType, createdAt, status",
  importResults: "id, importedAt, status, schemaName",
  meta: "key, updatedAt",
};

const VERSION_2_STORES = {
  privateSources:
    "id, recordType, occurredAt, createdAt, updatedAt, sourceMode, sensitivity, retentionPolicy, deletionState, exportAllowed, schemaVersion",
  structuredRecords:
    "id, recordType, effectiveDate, [recordType+effectiveDate], createdAt, updatedAt, status, deletionState, exportAllowed, schemaVersion, source.kind, *sourceRecordIds",
  externalSummaries:
    "id, summaryType, date, [date+status], createdAt, updatedAt, status, deletionState, exportAllowed, schemaVersion, *sourceStructuredRecordIds",
  constitutions: "id, version, status, createdAt, updatedAt, schemaVersion",
  profiles: "id, status, createdAt, updatedAt, schemaVersion",
  boardImports:
    "id, contractId, date, [date+status], importedAt, payloadHash, status, schemaVersion",
  jointCorrections:
    "id, date, [date+status], status, boardContextId, ceoReviewExportId, schemaVersion",
  auditEvents: "id, occurredAt, entityType, entityId, action, schemaVersion",
  exportManifests: "id, exportType, createdAt, status, schemaVersion",
  importResults: "id, importedAt, status, schemaName, schemaVersion",
  meta: "key, updatedAt",
};

const VERSION_3_STORES = {
  ...VERSION_2_STORES,
  structuredRecords:
    "id, recordType, effectiveDate, [recordType+effectiveDate], [recordType+effectiveDate+status], createdAt, updatedAt, status, deletionState, exportAllowed, schemaVersion, source.kind, *sourceRecordIds",
  profiles: "id, status, onboardingStatus, createdAt, updatedAt, schemaVersion",
};

const VERSION_4_STORES = {
  ...VERSION_3_STORES,
  structuredRecords:
    "id, recordType, effectiveDate, [recordType+effectiveDate], [recordType+effectiveDate+status], createdAt, updatedAt, status, deletionState, exportAllowed, schemaVersion, source.kind, promiseStatus, parkingStatus, truthInboxStatus, reviewDueAt, dueAt, *sourceRecordIds",
};

type MutableLegacyRecord = Record<string, unknown>;

function normalizeLegacyRecord(record: MutableLegacyRecord): void {
  record.schemaVersion ??= INTERNAL_SCHEMA_VERSION;
  record.deletionState ??= "active";
}

async function migrateVersion1ToVersion2(transaction: Transaction): Promise<void> {
  const entityStores = [
    "privateSources",
    "structuredRecords",
    "externalSummaries",
    "constitutions",
    "profiles",
    "jointCorrections",
  ] as const;
  for (const storeName of entityStores) {
    await transaction
      .table<MutableLegacyRecord, string>(storeName)
      .toCollection()
      .modify((record) => {
        normalizeLegacyRecord(record);
        if (storeName === "privateSources") {
          record.exportAllowed = false;
        }
      });
  }
  for (const storeName of [
    "boardImports",
    "auditEvents",
    "exportManifests",
    "importResults",
  ] as const) {
    await transaction
      .table<MutableLegacyRecord, string>(storeName)
      .toCollection()
      .modify((record) => {
        record.schemaVersion ??= INTERNAL_SCHEMA_VERSION;
      });
  }
  await transaction.table<DatabaseMetaRecord, string>("meta").put({
    key: "lastMigration",
    value: "1->2",
    updatedAt: new Date().toISOString(),
  });
}

async function migrateVersion2ToVersion3(transaction: Transaction): Promise<void> {
  await transaction
    .table<MutableLegacyRecord, string>("profiles")
    .toCollection()
    .modify((record) => {
      record.businessTimezone ??= record.timezone ?? "Asia/Shanghai";
      record.preferredLocale ??= "zh-CN";
      record.weekStartDay ??= 1;
      record.onboardingStatus ??= record.privacyAcknowledgedAt ? "completed" : "in_progress";
      record.onboardingStep ??= record.privacyAcknowledgedAt ? 5 : 1;
    });
  await transaction
    .table<MutableLegacyRecord, string>("structuredRecords")
    .toCollection()
    .modify((record) => {
      if (record.recordType === "daily_review") {
        record.reviewMode ??= "quick";
        record.reviewStatus ??= record.completedAt ? "completed" : "draft";
        record.draftStep ??= 0;
        record.privateSourceRecordId ??=
          Array.isArray(record.sourceRecordIds) && record.sourceRecordIds[0]
            ? record.sourceRecordIds[0]
            : record.id;
      }
      if (record.recordType === "behavior_correction") {
        record.behaviorToChange ??= record.action ?? "";
        record.affectedBusinessResult ??= "";
        record.sourceReviewId ??=
          Array.isArray(record.sourceRecordIds) && record.sourceRecordIds[0]
            ? record.sourceRecordIds[0]
            : record.id;
      }
    });
  await transaction.table<DatabaseMetaRecord, string>("meta").put({
    key: "lastMigration",
    value: "2->3",
    updatedAt: new Date().toISOString(),
  });
}

const IDEA_STATUS_MAP: Record<string, string> = {
  parking: "parked",
  keep_candidate: "under_review",
  rejected: "rejected",
  sent_to_board: "escalated_to_board",
};

const PROMISE_STATUS_MAP: Record<string, string> = {
  open: "active",
  fulfilled: "fulfilled",
  overdue: "overdue",
  renegotiated: "renegotiated",
  cancelled: "cancelled",
};

export function normalizeRound03StructuredRecord(record: MutableLegacyRecord): void {
  if (record.recordType === "evidence_statement") {
    if (record.status === "confirmed") record.status = "active";
    record.supportsStatementIds ??= [];
    record.contradictsStatementIds ??= [];
    record.sensitivity ??= "standard";
    record.truthInboxStatus ??= "none";
  }
  if (record.recordType === "idea_parking") {
    record.capturedAt ??= record.proposedAt ?? record.createdAt;
    record.captureSource ??= "manual";
    record.relatedMainline ??= record.relationshipToMainline ?? "";
    record.relatedConstraint ??= "";
    record.expectedValue ??= "";
    record.urgencyClaim ??= "";
    record.resourceCost ??= record.estimatedResource ?? "";
    record.opportunityCost ??= "";
    if (typeof record.parkingStatus === "string") {
      record.parkingStatus = IDEA_STATUS_MAP[record.parkingStatus] ?? record.parkingStatus;
    } else {
      record.parkingStatus = "parked";
    }
    if (record.status === "draft") record.status = "active";
    if (record.userConfirmation === "unconfirmed") record.userConfirmation = "confirmed";
  }
  if (record.recordType === "no_start") {
    const source = record.source as { kind?: unknown } | undefined;
    record.origin ??= source?.kind === "board_import" ? "board_import" : "manual_entry";
  }
  if (record.recordType === "promise") {
    record.counterpartyAlias ??= record.counterparty ?? "未命名对象";
    record.counterpartyType ??= "other";
    record.content ??= record.promiseContent ?? "";
    record.promisedAt ??= record.createdAt;
    record.originalDueAt ??= record.dueAt;
    record.businessTimezone ??= "Asia/Shanghai";
    record.materiality ??= "medium";
    record.trustImpact ??= "";
    record.promiseSource ??= "manual";
    record.confidentiality ??= "sensitive";
    record.history ??= [
      {
        id: crypto.randomUUID(),
        action: "created",
        occurredAt: record.createdAt,
      },
    ];
    if (typeof record.promiseStatus === "string") {
      record.promiseStatus = PROMISE_STATUS_MAP[record.promiseStatus] ?? record.promiseStatus;
    } else {
      record.promiseStatus = "active";
    }
    if (record.promiseStatus === "overdue") record.status = "overdue";
    else if (record.promiseStatus === "fulfilled") record.status = "completed";
    else if (record.promiseStatus === "cancelled") record.status = "cancelled";
    else if (record.status === "confirmed") record.status = "active";
  }
}

export function normalizeRound03ProfileRecord(record: MutableLegacyRecord): void {
  record.ideaCoolingHours ??= 72;
  record.governanceExportDefaults ??= {
    truth: "none",
    focus: "aggregate_only",
    promise: "risk_only",
  };
}

async function migrateVersion3ToVersion4(transaction: Transaction): Promise<void> {
  await transaction
    .table<MutableLegacyRecord, string>("profiles")
    .toCollection()
    .modify((record) => normalizeRound03ProfileRecord(record));
  await transaction
    .table<MutableLegacyRecord, string>("structuredRecords")
    .toCollection()
    .modify((record) => normalizeRound03StructuredRecord(record));
  await transaction.table<DatabaseMetaRecord, string>("meta").put({
    key: "lastMigration",
    value: "3->4",
    updatedAt: new Date().toISOString(),
  });
}

export class CEOOSDatabase extends Dexie {
  privateSources!: EntityTable<PrivateSourceRecord, "id">;
  structuredRecords!: EntityTable<StructuredCEORecord, "id">;
  externalSummaries!: EntityTable<ExternalSummaryRecord, "id">;
  constitutions!: EntityTable<ConstitutionVersion, "id">;
  profiles!: EntityTable<CEOProfile, "id">;
  boardImports!: EntityTable<StoredBoardContext, "id">;
  jointCorrections!: EntityTable<StoredJointCorrection, "id">;
  auditEvents!: EntityTable<AuditEvent, "id">;
  exportManifests!: EntityTable<DataExportManifest, "id">;
  importResults!: EntityTable<DataImportResult, "id">;
  meta!: EntityTable<DatabaseMetaRecord, "key">;

  constructor(name = DATABASE_NAME) {
    super(name);
    this.version(1).stores(VERSION_1_STORES);
    this.version(2).stores(VERSION_2_STORES).upgrade(migrateVersion1ToVersion2);
    this.version(3).stores(VERSION_3_STORES).upgrade(migrateVersion2ToVersion3);
    this.version(4).stores(VERSION_4_STORES).upgrade(migrateVersion3ToVersion4);
  }
}

export const db = new CEOOSDatabase();

export async function initializeDatabase(
  database: CEOOSDatabase = db,
  now = new Date().toISOString(),
): Promise<void> {
  await database.open();
  await database.transaction("rw", database.meta, database.constitutions, async () => {
    const initialized = await database.meta.get("initialized");
    if (initialized) {
      return;
    }
    await database.constitutions.put(createDefaultConstitution(now));
    await database.meta.bulkPut([
      {
        key: "initialized",
        value: true,
        updatedAt: now,
      },
      {
        key: "databaseVersion",
        value: DATABASE_VERSION,
        updatedAt: now,
      },
      {
        key: "schemaVersion",
        value: INTERNAL_SCHEMA_VERSION,
        updatedAt: now,
      },
    ]);
  });
}

export async function getRecordCounts(database: CEOOSDatabase = db): Promise<RecordCounts> {
  const [privateSources, structuredRecords, externalSummaries, boardImports, auditEvents] =
    await Promise.all([
      database.privateSources.count(),
      database.structuredRecords.count(),
      database.externalSummaries.count(),
      database.boardImports.count(),
      database.auditEvents.count(),
    ]);
  return {
    privateSources,
    structuredRecords,
    externalSummaries,
    boardImports,
    auditEvents,
  };
}

export async function deleteAllLocalData(database: CEOOSDatabase = db): Promise<void> {
  await database.transaction(
    "rw",
    [
      database.privateSources,
      database.structuredRecords,
      database.externalSummaries,
      database.constitutions,
      database.profiles,
      database.boardImports,
      database.jointCorrections,
      database.auditEvents,
      database.exportManifests,
      database.importResults,
      database.meta,
    ],
    async () => {
      await Promise.all(database.tables.map((table) => table.clear()));
    },
  );
}

export const databaseStoreDefinitions = {
  version1: VERSION_1_STORES,
  version2: VERSION_2_STORES,
  version3: VERSION_3_STORES,
  version4: VERSION_4_STORES,
} as const;
